import FormativeForm from 'components/CreateFormative/FormativeForm';
import { useApp } from 'contexts/app';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import formativeService from 'services/formative';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ResponsiveDialog,
    Stack,
    Text,
} from 'shell/components';

const EditFormativeDialog = ({ open, onClose }) => {
    const { currentAssignment, formative, setFormative } = useApp();
    const { message } = useSnackbar();
    const { t } = useTranslation();

    const [form, _setForm] = useState(null);
    const [errors, setErrors] = useState({ topic: '' });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const hasAdditionalSettings = useMemo(
        () =>
            ['rubric', 'system'].includes(
                currentAssignment?.ai_student_feedback_settings?.structure ??
                    currentAssignment?.ai_feedback_settings?.structure
            ),
        [currentAssignment?.ai_feedback_settings?.structure, currentAssignment?.ai_student_feedback_settings?.structure]
    );

    const validateAll = useCallback(() => {
        const isTopicValid = Boolean(form?.topic);
        setErrors({ topic: !isTopicValid ? 'This field is required.' : '' });
        return isTopicValid;
    }, [form?.topic]);

    const setForm = useCallback(
        (param) => {
            if (hasClickedSubmit) validateAll();
            _setForm(param);
        },
        [hasClickedSubmit, validateAll]
    );

    const onClickSubmit = async () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (!isValid) return;
        const timestamp = new Date().getTime();
        const updatedFormative = {
            ...formative,
            modified_on: timestamp,
            ai_context: form.topic,
            ...(form.settings && {
                ai_student_feedback_settings: {
                    suggestion_length: form.settings.suggestionLength,
                    language_level: form.settings.languageLevel,
                    tone: form.settings.tone,
                },
            }),
        };
        try {
            setIsLoading(true);
            await formativeService.updateFormative(formative.id, omit(updatedFormative, ['_id']));
            setFormative(updatedFormative);
            message.success('Your parameters have been saved.');
            onClose();
        } catch (e) {
            message.error(t('common:message.something-went-wrong', 'Something went wrong.'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        _setForm({
            topic: formative.ai_context,
            settings: hasAdditionalSettings
                ? {
                      suggestionLength: formative.ai_student_feedback_settings.suggestion_length,
                      languageLevel: formative.ai_student_feedback_settings.language_level,
                      tone: formative.ai_student_feedback_settings.tone,
                  }
                : null,
        });
    }, [formative, hasAdditionalSettings]);

    return (
        <ResponsiveDialog aria-labelledby={'dialog-title'} open={open} scroll={'paper'} onClose={onClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text id={'dialog-title'} component={'h1'} variant={'h6'} weight={'semibold'}>
                        Edit Draft Parameters
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={4}>
                    <Text>Change your details for AI to give the most accurate feedback.</Text>
                    <FormativeForm form={form} setForm={setForm} errors={errors} />
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} justifyContent={'flex-end'} spacing={3}>
                    <Button color={'neutral'} variant={'tertiary'} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button loading={isLoading} onClick={onClickSubmit}>
                        Save
                    </Button>
                </Stack>
            </DialogActions>
        </ResponsiveDialog>
    );
};

export default EditFormativeDialog;
